import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * ImageViewer Component
 * Displays images centered and fit to screen with zoom capability.
 *
 * @param {Object} props
 * @param {string} props.documentUrl - Authenticated URL to the image
 * @param {string} props.filename - Name of the image file
 * @returns {React.ReactElement} Image viewer
 */
const ImageViewer = ({ documentUrl, filename }) => {
    const theme = useTheme();
    const [imageError, setImageError] = useState(false);

    const handleImageError = (error) => {
        console.error('Error loading image:', error);
        setImageError(true);
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: theme.palette.grey[900],
                overflow: 'auto',
                p: { xs: 1, sm: 2, md: 3 },
            }}
        >
            {imageError ? (
                <Box
                    sx={{
                        textAlign: 'center',
                        backgroundColor: theme.palette.grey[800],
                        color: theme.palette.common.white,
                        p: { xs: 3, sm: 4 },
                        borderRadius: 2,
                        maxWidth: 500,
                        boxShadow: theme.shadows[4],
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        Failed to load image
                    </Typography>
                    <Typography variant="body2" color="grey.300">
                        The image could not be displayed. Please try downloading it instead.
                    </Typography>
                </Box>
            ) : (
                <img
                    src={documentUrl}
                    alt={filename}
                    onError={handleImageError}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        display: 'block',
                        borderRadius: '8px',
                        boxShadow: theme.shadows[10],
                    }}
                />
            )}
        </Box>
    );
};

ImageViewer.propTypes = {
    documentUrl: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
};

export default ImageViewer;
